import React, { useState, useCallback, useEffect } from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { GridCellParams, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Clear } from '@mui/icons-material';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { formatDate } from 'src/shared/functions/formatDate';
import { getAllEvents } from 'src/shared/services/eventService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { Event } from '../models/event';
import { EventDateFilter, EventSearchFilter } from '../models/eventSearchFilter';
import { EventOverviewGridFooterButton } from './eventOverviewGridFooterButton';

type Props = {
  searchFilter: EventSearchFilter;
  dateFilter: EventDateFilter;
};

const EventsDataGrid = (props: Props) => {
  const { searchFilter, dateFilter } = props;
  const { t } = useTranslation(['event', 'common']);
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [loadingPDF, setLoadingPDF] = useState<boolean>(false);
  const [rows, setRows] = useState<Event[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: false,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    setTimeout(() => {
      scrollIntoView();
    }, 100);
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    try {
      const res = await getAllEvents(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort, searchFilter, dateFilter);
      if (isMounted()) {
        updateData('totalRows', res.data.totalItems);
        setRows(res.data.results);
      }
      ConsoleHelper.log(res);
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [data.page, data.pageSize, isMounted, sortModel, searchFilter, dateFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const pdfLoadingCallback = useCallback((isLoading: boolean) => {
    setLoadingPDF(isLoading);
  }, []);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.Events}/${params.row.id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('event:name'),
      flex: 2,
    },
    {
      field: 'date',
      headerName: t('event:date'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const { date } = params.row;
        return formatDate(date);
      },
    },
    {
      field: 'city',
      headerName: t('event:postalAddress'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return params.row.city !== null ? <>{params.row.city}</> : null;
      },
    },
    {
      field: 'lastDateForApplication',
      headerName: t('event:applicationDeadline'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const { lastDateForApplication } = params.row;
        return formatDate(lastDateForApplication);
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <LoadingOverlay message={t('invoice:loadingPdf')} loading={loadingPDF} />
      <DataGrid<Event>
        autoHeight
        columns={columns}
        rows={rows}
        dataModel={data}
        checkboxSelection
        disableSelectionOnClick
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        selectionModel={selectionModel}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
        onRowClick={onRowClick}
      />
      {selectionModel && selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }} size='large'>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            <EventOverviewGridFooterButton selectionModel={selectionModel} fetchData={fetchData} setSelectionModel={setSelectionModel} />
          </Box>
        </FixedFooter>
      )}
    </Box>
  );
};

export default EventsDataGrid;
